import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search, LogOut, UserRound, Shield, ChevronDown, Download, Heart, Bookmark, Settings as SettingsIcon, Film, Calendar, ArrowUpDown, Globe } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import TraktImportModal from "@/components/TraktImportModal";
import NotificationsBell from "@/components/NotificationsBell";
import MessagesBell from "@/components/MessagesBell";
import { useLists } from "@/contexts/ListsContext";
import { REGIONS, getRegion, setRegion as setStoredRegion } from "@/lib/region";

const YEARS = ["", ...Array.from({ length: 26 }, (_, i) => String(2026 - i))];
const SORTS = [
  { value: "popularity.desc", label: "Most Popular" },
  { value: "vote_average.desc", label: "Highest Rated" },
  { value: "primary_release_date.desc", label: "Newest" },
  { value: "primary_release_date.asc", label: "Oldest" },
];

export default function Header({ genres = [] }) {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { refresh } = useLists();
  const [q, setQ] = useState(sp.get("q") || "");
  const [traktOpen, setTraktOpen] = useState(false);
  const [region, setRegion] = useState(getRegion());

  const onRegionChange = (e) => {
    const v = e.target.value;
    setRegion(v);
    setStoredRegion(v);
  };

  const setParam = (key, val) => {
    const next = new URLSearchParams(sp);
    if (val) next.set(key, val); else next.delete(key);
    // Switching filters jumps to discover mode
    if (["genre", "year", "sort"].includes(key) && val) next.set("cat", "discover");
    next.delete("page");
    setSp(next);
  };

  const onSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(sp);
    if (q.trim()) {
      next.set("q", q.trim());
      next.set("cat", "search");
    } else {
      next.delete("q");
      next.set("cat", "popular");
    }
    setSp(next);
  };

  return (
    <header className="sticky top-0 z-40 w-full backdrop-blur-xl bg-black/80 border-b border-[var(--tt-border)] px-4 md:px-6 py-3 flex flex-wrap items-center gap-3">
      <form onSubmit={onSearch} className="flex items-center gap-2 flex-1 min-w-[200px] max-w-xl bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-full px-4 py-2 focus-within:border-[var(--tt-primary)] transition-colors">
        <Search className="w-4 h-4" style={{ color: "#06B6D4" }} />
        <input
          data-testid="top-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search movies, TV shows..."
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-[var(--tt-text-faint)]"
        />
      </form>

      <label className="flex items-center gap-1.5 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-full pl-3 pr-1 hover:border-[var(--tt-primary)] transition-colors">
        <Film className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#EF4444" }} />
        <select
          data-testid="header-filter-all-genres"
          value={sp.get("genre") || ""}
          onChange={(e) => setParam("genre", e.target.value)}
          className="bg-transparent rounded-full pr-2 py-2 text-xs text-white outline-none cursor-pointer min-w-[110px]"
        >
          <option value="">All Genres</option>
          {genres.map((g) => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>
      </label>

      <label className="flex items-center gap-1.5 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-full pl-3 pr-1 hover:border-[var(--tt-primary)] transition-colors">
        <Calendar className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#6D4AFF" }} />
        <select
          data-testid="header-filter-all-years"
          value={sp.get("year") || ""}
          onChange={(e) => setParam("year", e.target.value)}
          className="bg-transparent rounded-full pr-2 py-2 text-xs text-white outline-none cursor-pointer min-w-[90px]"
        >
          <option value="">All Years</option>
          {YEARS.filter(Boolean).map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </label>

      <label className="flex items-center gap-1.5 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-full pl-3 pr-1 hover:border-[var(--tt-primary)] transition-colors">
        <ArrowUpDown className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#FFD700" }} />
        <select
          data-testid="header-filter-sort"
          value={sp.get("sort") || "popularity.desc"}
          onChange={(e) => setParam("sort", e.target.value)}
          className="bg-transparent rounded-full pr-2 py-2 text-xs text-white outline-none cursor-pointer min-w-[130px]"
        >
          {SORTS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </label>

      <label className="flex items-center gap-1.5 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-full pl-3 pr-1 hover:border-[var(--tt-primary)] transition-colors" title="Streaming region — used for the 'Streaming on' row">
        <Globe className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#22C77A" }} />
        <select
          data-testid="header-region"
          value={region}
          onChange={onRegionChange}
          className="bg-transparent rounded-full pr-2 py-2 text-xs text-white outline-none cursor-pointer"
        >
          {REGIONS.map((r) => (
            <option key={r.code} value={r.code}>{r.flag} {r.code}</option>
          ))}
        </select>
      </label>

      {user ? (
        <>
        <MessagesBell />
        <NotificationsBell />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-menu-dropdown"
              className="flex items-center gap-2 bg-[var(--tt-surface-2)] hover:bg-[#222] border border-[var(--tt-border)] rounded-full pl-2 pr-3 py-1 transition-colors"
            >
              {user.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[var(--tt-primary)] to-[var(--tt-purple)] flex items-center justify-center text-xs font-bold">
                  {(user.name || user.email)[0].toUpperCase()}
                </div>
              )}
              <span className="text-xs font-semibold max-w-[100px] truncate hidden sm:inline">
                {user.name || user.email}
              </span>
              <ChevronDown className="w-3 h-3 text-[var(--tt-text-muted)]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-[var(--tt-surface)] border-[var(--tt-border)] text-white min-w-[200px]"
          >
            <DropdownMenuLabel className="text-xs text-[var(--tt-text-muted)]">
              {user.email}
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-[var(--tt-border)]" />
            <DropdownMenuItem onClick={() => navigate(`/u/${user.user_id}`)} className="cursor-pointer focus:bg-white/5" data-testid="user-menu-profile">
              <UserRound className="w-4 h-4 mr-2 text-[var(--tt-purple)]" />
              My Profile
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/watchlist")} className="cursor-pointer focus:bg-white/5" data-testid="user-menu-watchlist">
              <Bookmark className="w-4 h-4 mr-2 text-[var(--tt-yellow)]" />
              Watchlist
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/favorites")} className="cursor-pointer focus:bg-white/5" data-testid="user-menu-favorites">
              <Heart className="w-4 h-4 mr-2 text-[var(--tt-primary)]" />
              Favorites
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTraktOpen(true)} className="cursor-pointer focus:bg-white/5" data-testid="user-menu-trakt-import">
              <Download className="w-4 h-4 mr-2" style={{ color: "#FF6B35" }} />
              Import from Trakt
            </DropdownMenuItem>
            {(user.role === "admin" || user.role === "mod") && (
              <DropdownMenuItem
                onClick={() => navigate("/admin")}
                className="cursor-pointer focus:bg-white/5"
                data-testid="user-menu-admin"
              >
                <Shield className="w-4 h-4 mr-2 text-[var(--tt-yellow)]" />
                {user.role === "admin" ? "Admin Panel" : "Mod Panel"}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => navigate("/settings")}
              className="cursor-pointer focus:bg-white/5"
              data-testid="user-menu-settings"
            >
              <SettingsIcon className="w-4 h-4 mr-2" style={{ color: "#06B6D4" }} />
              Settings
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={logout}
              className="cursor-pointer focus:bg-white/5 text-red-400"
              data-testid="user-menu-logout"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        </>
      ) : (
        <div className="flex items-center gap-2">
          <Link
            to="/login"
            data-testid="header-login-link"
            className="text-xs font-semibold px-4 py-2 rounded-full text-white hover:bg-white/5"
          >
            Login
          </Link>
          <Link
            to="/register"
            data-testid="header-register-link"
            className="text-xs font-semibold px-4 py-2 rounded-full bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white transition-colors"
          >
            Sign up
          </Link>
        </div>
      )}
      <TraktImportModal open={traktOpen} onClose={() => setTraktOpen(false)} onDone={refresh} />
    </header>
  );
}
